(function () {
  const EDGE_GUARD = 24;
  const LOCK_DISTANCE = 10;
  const HORIZONTAL_BIAS = 1.2;
  const RESIST = 0.35;
  const EXIT_MS = 280;
  const TOAST_MS = 5000;
  const INTERACTIVE = 'button, a, input, select, textarea, label, summary, details';
  const NUMBER_WORDS = { one: 1, single: 1, trial: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };

  let addsInFlight = 0;

  // UpCart intercepts every /cart/add request and opens its drawer. Swipe adds must stay in the feed, so skip UpCart's handling only while one of our adds is in flight.
  function hookUpcart() {
    if (window.__shSwipeMatchUpcartHooked) return;
    window.__shSwipeMatchUpcartHooked = true;
    const previous = window.upcartShouldSkipAddToCart;
    window.upcartShouldSkipAddToCart = function (request) {
      if (addsInFlight > 0) return true;
      return typeof previous === 'function' ? previous(request) : false;
    };
  }

  function track(action, detail) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ event: 'swipe_match', swipe_action: action, ...detail });
  }

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, fraction] = value.split('.');
    const withCommas = Number(whole).toLocaleString('en-US');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas.replaceAll(',', '.') + ',' + fraction)
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + fraction)
      .replace(/<[^<>]*>/g, '');
  }

  // Variant titles name the bottle count in several ways ("4 Bottles", "Two Bottles", "Trial Bottle",
  // "BUY 2 + GET 2 EXTRA FREE"); the sum of the numbers is what ships.
  function bottlesOf(title) {
    if (!title || title === 'Default Title') return 0;
    const digits = title.match(/\d+/g);
    if (digits) return digits.reduce((sum, n) => sum + Number(n), 0);
    const word = title.toLowerCase().match(/\b(one|single|trial|two|three|four|five|six)\b/);
    return word ? NUMBER_WORDS[word[1]] : 0;
  }

  function fill(template, values) {
    return (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  class SwipeMatch {
    constructor(root) {
      this.root = root;
      this.threshold = (Number.parseFloat(root.dataset.threshold) || 28) / 100;
      this.moneyFormat = root.dataset.moneyFormat;
      this.freeShipping = Number(root.dataset.freeShipping) || 0;
      this.upsellTrigger = Number(root.dataset.upsellTrigger) || 0;
      this.upsellMax = Number(root.dataset.upsellMax) || 0;
      this.giftConfig = this.readJson(root.dataset.giftConfig);
      this.text = this.readJson(root.querySelector('[data-swipe-text]')?.textContent) || {};
      this.storageKey = 'sh-swipe-match-v1:' + root.dataset.collectionId;
      this.cards = Array.from(root.querySelectorAll('[data-card]')).map((node) => this.readCard(node));
      this.byId = new Map(this.cards.map((c) => [c.id, c]));
      this.cart = null;
      this.addQueue = Promise.resolve();
      this.drag = null;
      this.toastTimer = null;
      this.lastFocus = null;
      this.viewer = null;
      this.sheet = null;
      this.state = this.loadState();
      this.reconcileOrder();
      this.bind();
      this.render();
    }

    start() {
      this.wireUpcartEvents();
      return this.refreshCart();
    }

    c(name) {
      return 'custom-swipe-match-' + name;
    }

    readJson(source) {
      try {
        return source ? JSON.parse(source) : null;
      } catch {
        return null;
      }
    }

    giftsApply(card, variant) {
      if (!card.data.gifts || !this.giftConfig) return false;
      if (this.giftConfig.appliesTo === 'subscription_only') return Boolean(variant.planId);
      if (this.giftConfig.appliesTo === 'otp_only') return !variant.planId;
      return true;
    }

    // Mirrors the product page (ctr-free-gift.js): the gift lines are nested under the product line and both carry _shipping_offer.
    lineRef(card, variant) {
      const item = { id: variant.id, quantity: 1 };
      if (variant.planId) item.selling_plan = variant.planId;
      const ref = { item, variantId: variant.id, planId: variant.planId || null, shippingOffer: null, giftItems: [] };
      const config = this.giftConfig;
      if (!this.giftsApply(card, variant) || !config.addToCart || !config.variantIds.length) return ref;
      ref.shippingOffer = config.shippingOffer;
      item.properties = { _shipping_offer: config.shippingOffer };
      ref.giftItems = config.variantIds.map((id) => ({
        id,
        quantity: 1,
        parent_id: variant.id,
        properties: { _offer: config.propertyValue, _shipping_offer: config.shippingOffer },
      }));
      return ref;
    }

    isGiftLine(item) {
      if (item.properties?._offer) return true;
      return Boolean(this.giftConfig) && this.giftConfig.variantIds.includes(item.variant_id);
    }

    // The store's "Free Gift" discount covers one gift per order; a second gift line is charged at full price.
    async restoreGift() {
      const config = this.giftConfig;
      if (!config?.addToCart || !config.variantIds.length) return;
      const cart = await this.fetchCart();
      if (cart.items.some((i) => this.isGiftLine(i))) return;
      const parent = cart.items.find((i) => {
        if (this.isGiftLine(i) || i.properties?._shipping_offer !== config.shippingOffer) return false;
        if (config.appliesTo === 'subscription_only') return Boolean(this.planOf(i));
        if (config.appliesTo === 'otp_only') return !this.planOf(i);
        return true;
      });
      if (!parent) return;
      const items = config.variantIds.map((id) => ({
        id,
        quantity: 1,
        parent_id: parent.variant_id,
        properties: { _offer: config.propertyValue, _shipping_offer: config.shippingOffer },
      }));
      addsInFlight++;
      try {
        await fetch(this.root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
      } finally {
        addsInFlight--;
      }
    }

    matchesLine(item, ref) {
      const offer = item.properties?._shipping_offer || null;
      return item.variant_id === ref.variantId && this.planOf(item) === ref.planId && offer === ref.shippingOffer;
    }

    readCard(node) {
      const data = JSON.parse(node.querySelector('[data-card-data]').textContent);
      return {
        el: node,
        id: String(data.id),
        data,
        track: node.querySelector('[data-track]'),
        media: node.querySelector('[data-gallery]'),
        segments: node.querySelector('[data-segments]'),
        counter: node.querySelector('[data-counter]'),
        galleryOption: String(data.id),
        pending: false,
      };
    }

    loadState() {
      const fallback = { order: [], decisions: {}, variants: {}, flavors: {}, gal: {}, tipDismissed: false };
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        return saved ? { ...fallback, ...saved } : fallback;
      } catch {
        return fallback;
      }
    }

    saveState() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch {
        // Storage can be full or blocked (private mode); progress just isn't saved.
      }
    }

    reconcileOrder() {
      const ids = this.cards.map((c) => c.id);
      const kept = this.state.order.filter((id) => ids.includes(id));
      ids.forEach((id) => {
        if (!kept.includes(id)) kept.push(id);
      });
      this.state.order = kept;
      Object.keys(this.state.decisions).forEach((id) => {
        if (!ids.includes(id)) delete this.state.decisions[id];
      });
    }

    bind() {
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onKeyDown = this.handleDocumentKey.bind(this);

      this.cards.forEach((card) => {
        card.el.addEventListener('pointerdown', (e) => this.handlePointerDown(card, e));
        card.el.addEventListener('keydown', (e) => this.handleCardKey(card, e));
        card.el.addEventListener('dragstart', (e) => e.preventDefault());
        card.el.addEventListener(
          'touchmove',
          (e) => {
            if (this.drag?.card === card && this.drag.lock === 'x' && e.cancelable) e.preventDefault();
          },
          { passive: false },
        );
      });

      this.bindSheetDrag();

      this.root.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]');
        if (!target || !this.root.contains(target) || target.disabled) return;
        const card = this.cardFor(target);
        switch (target.dataset.action) {
          case 'dismiss-tip':
            this.state.tipDismissed = true;
            this.saveState();
            this.render();
            break;
          case 'pass':
            if (this.current()) this.pass(this.current(), 'button');
            break;
          case 'choose':
            if (this.current()) this.openDeal(this.current(), 'button', target);
            break;
          case 'deal':
            if (card) this.selectVariant(card, target.dataset.variantId);
            break;
          case 'flavor':
            if (card) this.selectFlavor(card, target.dataset.flavorId);
            break;
          case 'deal-add':
            this.addFromDeal();
            break;
          case 'gallery-prev':
            if (card) this.setGallery(card, (this.state.gal[card.id] || 0) - 1);
            break;
          case 'gallery-next':
            if (card) this.setGallery(card, (this.state.gal[card.id] || 0) + 1);
            break;
          case 'enlarge':
            if (card) this.openViewer(card, target);
            break;
          case 'nutrition':
            if (card) this.openNutrition(card, target);
            break;
          case 'open-skipped':
            this.openSkipped(target);
            break;
          case 'skipped-add':
            this.addSkipped(target);
            break;
          case 'open-bag':
            this.openBag(target);
            break;
          case 'bag-qty':
            this.changeQuantity(target.dataset.key, Number(target.dataset.quantity));
            break;
          case 'reset':
            this.reset();
            break;
          case 'restart':
            this.restart();
            break;
          case 'close-sheet':
            this.closeSheet();
            break;
          case 'close-viewer':
            this.closeViewer();
            break;
          case 'viewer-prev':
            this.stepViewer(-1);
            break;
          case 'viewer-next':
            this.stepViewer(1);
            break;
        }
      });

      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      document.addEventListener('keydown', this.onKeyDown);
    }

    destroy() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('keydown', this.onKeyDown);
      clearTimeout(this.toastTimer);
      document.documentElement.style.overflow = '';
    }

    cardFor(target) {
      const node = target.closest('[data-card]');
      if (node) return this.byId.get(node.dataset.productId) || null;
      if (this.sheet?.card && target.closest('[data-sheet]')) return this.sheet.card;
      return null;
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    q(selector) {
      return this.root.querySelector(selector);
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    shortMoney(cents) {
      return this.money(cents).replace(/[.,]00(?=\D*$)/, '');
    }

    feed() {
      return this.state.order.map((id) => this.byId.get(id)).filter((c) => c && !this.state.decisions[c.id]);
    }

    current() {
      return this.feed()[0] || null;
    }

    optionOf(card) {
      const options = card.data.options;
      const chosen = this.state.flavors[card.id];
      return (
        options.find((o) => String(o.id) === String(chosen)) ||
        options.find((o) => String(o.id) === card.id) ||
        options[0]
      );
    }

    variantOf(card) {
      const option = this.optionOf(card);
      const chosen = this.state.variants[card.id];
      const variants = option.variants;
      return (
        variants.find((v) => String(v.id) === String(chosen) && v.available) ||
        variants.find((v) => v.id === option.defaultVariantId && v.available) ||
        variants.find((v) => v.available) ||
        variants[0]
      );
    }

    // Plan names vary across products ("Deliver Every 1 Month", "Delivery every 3 months", "Every 1 Month, $59.95 Flat Rate").
    intervalOf(planName) {
      const match = (planName || '').match(/every\s+(\d+)?\s*(day|week|month|year)s?/i);
      if (!match) return (planName || '').toLowerCase();
      const count = Number(match[1] || 1);
      const unit = match[2].toLowerCase();
      return 'every ' + count + ' ' + unit + (count === 1 ? '' : 's');
    }

    isSoldOut(card) {
      return !this.optionOf(card).available;
    }

    passedCards() {
      return this.state.order
        .map((id) => this.byId.get(id))
        .filter((c) => c && this.state.decisions[c.id] === 'passed');
    }

    bagItems() {
      return this.cart ? this.cart.items.filter((i) => !this.isGiftLine(i)) : [];
    }

    bagCount() {
      return this.bagItems().reduce((sum, i) => sum + i.quantity, 0);
    }

    qualifiesForFreeShipping(cents) {
      return this.freeShipping > 0 && cents >= this.freeShipping;
    }

    // Deal tiles: biggest bundle first. Savings compare against the dearest per-bottle price of the same product,
    // from unrounded prices so the badge never claims more than the real difference.
    dealsOf(option) {
      const deals = option.variants.map((v, index) => {
        const count = bottlesOf(v.title);
        return { variant: v, index, count, each: count ? Math.round(v.price / count) : null, save: 0, badge: false };
      });
      const unitMax = Math.max(0, ...deals.filter((d) => d.count).map((d) => d.variant.price / d.count));
      deals.forEach((d) => {
        if (d.count) d.save = Math.floor(unitMax * d.count - d.variant.price);
      });
      deals
        .filter((d) => d.save >= 100 && d.variant.available)
        .sort((a, b) => b.save - a.save)
        .slice(0, 2)
        .forEach((d) => {
          d.badge = true;
        });
      return deals.sort((a, b) => b.count - a.count || a.index - b.index);
    }

    dealName(variant) {
      return variant.title === 'Default Title' ? '1 Bottle' : variant.title;
    }

    render() {
      const current = this.current();
      const total = this.cards.length;
      const explored = total - this.feed().length;
      const passed = this.passedCards().length;

      const tip = this.q('[data-tip]');
      if (tip) tip.hidden = this.state.tipDismissed;

      this.q('[data-progress-text]').textContent = explored + ' of ' + total + ' explored';
      this.q('[data-progress-bar]').style.width = (total ? (explored / total) * 100 : 0) + '%';
      this.q('[data-skipped-label]').textContent = 'Skipped (' + passed + ')';
      this.q('[data-reset]').hidden = explored === 0;

      this.cards.forEach((card) => {
        const isCurrent = card === current;
        if (card.el.hidden === isCurrent) {
          card.el.hidden = !isCurrent;
          if (isCurrent) this.enter(card);
        }
        this.renderCard(card);
      });

      const next = this.feed()[1];
      if (next) {
        const img = next.el.querySelector('img');
        if (img) img.loading = 'eager';
      }

      const actions = this.q('[data-actions]');
      actions.hidden = !current;
      if (current) {
        const choose = actions.querySelector('[data-action="choose"]');
        const soldOut = this.isSoldOut(current);
        choose.disabled = soldOut;
        choose.querySelector('[data-choose-text]').textContent = soldOut ? this.text.soldOut : this.text.hintChoose;
      }

      this.renderEnd(current, passed);
      this.renderBagBar();
      if (this.sheet?.kind === 'deal') this.renderDealSheet();
    }

    renderEnd(current, passed) {
      const count = this.bagCount();
      const bagEnd = this.q('[data-end="bag"]');
      const emptyEnd = this.q('[data-end="empty"]');
      bagEnd.hidden = !!current || count === 0;
      emptyEnd.hidden = !!current || count > 0;
      if (current) return;
      let text = fill(this.text.endBag, { bag: count });
      if (passed > 0) text += ' ' + fill(this.text.endSkipped, { skipped: passed });
      bagEnd.querySelector('[data-end-text]').textContent = text.trim();
      bagEnd.querySelector('[data-end-total]').textContent = this.cart ? this.money(this.cart.total_price) : '';
      this.root.querySelectorAll('[data-end-skipped]').forEach((btn) => {
        btn.hidden = passed === 0;
        btn.textContent = 'Review skipped (' + passed + ')';
      });
    }

    renderCard(card) {
      const option = this.optionOf(card);
      const variant = this.variantOf(card);
      const node = card.el;
      const soldOut = !option.available;
      node.classList.toggle('is-sold-out', soldOut);
      node.querySelector('[data-soldout-badge]').hidden = !soldOut;
      node.querySelector('[data-title]').textContent = option.title;

      node.querySelector('[data-price]').textContent = variant.priceText;
      const compare = node.querySelector('[data-compare]');
      compare.hidden = !variant.compareText;
      compare.textContent = variant.compareText || '';

      node.querySelector('[data-billed]').textContent = variant.planId
        ? fill(this.text.billed, { price: variant.priceText, interval: this.intervalOf(variant.planName) })
        : this.text.oneTime;
      const giftsApply = this.giftsApply(card, variant);
      const giftBadge = node.querySelector('[data-gift-badge]');
      if (giftBadge) giftBadge.hidden = !giftsApply;

      const list = node.querySelector('[data-checklist]');
      const lines = [(variant.title === 'Default Title' ? '1x' : variant.title) + ' ' + option.title];
      if (option.servings) lines[0] += ' ' + option.servings;
      if (giftsApply) this.giftNames().forEach((name) => lines.push('FREE ' + name));
      if (variant.planId && this.text.perk) lines.push(this.text.perk);
      const icon = this.q('[data-icon-check]').content;
      list.replaceChildren(
        ...lines.map((text) => {
          const li = el('li');
          li.appendChild(icon.cloneNode(true));
          li.appendChild(el('span', '', text));
          return li;
        }),
      );

      this.renderGallery(card);
    }

    giftNames() {
      return this.text.giftNames || [];
    }

    renderOffer(scope, card) {
      const option = this.optionOf(card);
      const variant = this.variantOf(card);
      const wrap = scope.querySelector('[data-deals-wrap]');
      const grid = scope.querySelector('[data-deals]');
      if (wrap && grid) {
        wrap.hidden = option.variants.length < 2;
        if (option.variants.length > 1) this.renderDeals(grid, option, variant);
      }
      this.renderFlavors(scope, card);
      scope.querySelectorAll('[data-flavor-id]').forEach((btn) => {
        const flavor = card.data.options.find((o) => String(o.id) === btn.dataset.flavorId);
        const checked = flavor === option;
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
        btn.tabIndex = checked ? 0 : -1;
        btn.disabled = !flavor?.available;
      });
      const gifts = scope.querySelector('[data-gifts]');
      if (gifts) gifts.hidden = !this.giftsApply(card, variant);
      const nutrition = scope.querySelector('[data-action="nutrition"]');
      if (nutrition) nutrition.hidden = !option.nutrition;
    }

    renderFlavors(scope, card) {
      const wrap = scope.querySelector('[data-flavors]');
      const grid = scope.querySelector('[data-flavor-grid]');
      if (!wrap || !grid) return;
      wrap.hidden = card.data.options.length < 2;
      if (wrap.hidden || grid.dataset.flavorsFor === card.id) return;
      grid.dataset.flavorsFor = card.id;
      grid.replaceChildren(
        ...card.data.options.map((option) => {
          const btn = el('button', this.c('flavor'));
          btn.type = 'button';
          btn.dataset.action = 'flavor';
          btn.dataset.flavorId = option.id;
          btn.setAttribute('role', 'radio');
          if (option.image) {
            const img = el('img', this.c('flavor-image'));
            img.src = option.image;
            img.alt = '';
            img.width = 48;
            img.height = 48;
            btn.appendChild(img);
          }
          btn.appendChild(el('span', this.c('flavor-name'), option.label));
          return btn;
        }),
      );
    }

    renderDeals(grid, option, selected) {
      const key = option.id + ':' + selected.id;
      if (grid.dataset.key === key) return;
      grid.dataset.key = key;
      const tiles = this.dealsOf(option).map((deal) => {
        const v = deal.variant;
        const btn = el('button', this.c('deal'));
        btn.type = 'button';
        btn.dataset.action = 'deal';
        btn.dataset.variantId = v.id;
        btn.setAttribute('role', 'radio');
        const checked = v.id === selected.id;
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
        btn.tabIndex = checked ? 0 : -1;
        btn.disabled = !v.available;
        if (deal.badge)
          btn.appendChild(
            el('span', this.c('deal-badge'), fill(this.text.dealBadge, { amount: this.shortMoney(deal.save) })),
          );
        const text = el('span', this.c('deal-text'));
        text.appendChild(el('span', this.c('deal-name'), this.dealName(v)));
        let sub = v.priceText;
        if (!v.available) sub = this.text.soldOut;
        else if (deal.each) sub = this.money(deal.each) + '/each';
        text.appendChild(el('span', this.c('deal-each'), sub));
        btn.appendChild(text);
        const src = v.image || option.image;
        if (src) {
          const img = el('img', this.c('deal-image'));
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 120;
          img.height = 120;
          btn.appendChild(img);
        }
        return btn;
      });
      grid.replaceChildren(...tiles);
    }

    renderGallery(card) {
      const option = this.optionOf(card);
      if (card.galleryOption !== String(option.id)) this.buildGallery(card, option);
      const count = option.images.length;
      if (!count) return;
      const index = Math.min(this.state.gal[card.id] || 0, count - 1);
      card.track.style.transform = 'translateX(' + -index * 100 + '%)';
      Array.from(card.segments?.children || []).forEach((s, i) => s.classList.toggle('is-active', i === index));
      if (card.counter) card.counter.textContent = index + 1 + ' / ' + count;
      const prev = card.el.querySelector('[data-action="gallery-prev"]');
      const next = card.el.querySelector('[data-action="gallery-next"]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === count - 1;
      const img = card.track.children[index]?.querySelector('img');
      if (img) img.loading = 'eager';
    }

    // Flavors are separate products, so switching one swaps the gallery to that product's photos.
    buildGallery(card, option) {
      card.galleryOption = String(option.id);
      const images = option.images;
      card.track.replaceChildren(
        ...images.map((image, i) => {
          const slide = el('div', this.c('slide'));
          const img = el('img', this.c('image'));
          img.src = image.thumb;
          img.alt = image.alt;
          img.width = 800;
          img.height = 800;
          img.draggable = false;
          img.loading = i === 0 ? 'eager' : 'lazy';
          slide.appendChild(img);
          return slide;
        }),
      );
      const many = images.length > 1;
      if (card.segments) {
        card.segments.hidden = !many;
        card.segments.replaceChildren(...images.map(() => el('span', this.c('segment'))));
      }
      card.el.querySelectorAll('[data-gallery-many]').forEach((n) => {
        n.hidden = !many;
      });
      const enlarge = card.el.querySelector('[data-action="enlarge"]');
      if (enlarge) enlarge.hidden = images.length === 0;
    }

    setGallery(card, index) {
      const count = this.optionOf(card).images.length;
      if (!count) return;
      const next = Math.max(0, Math.min(count - 1, index));
      if (next === (this.state.gal[card.id] || 0)) return;
      this.state.gal[card.id] = next;
      this.saveState();
      this.renderGallery(card);
      track('gallery_change', { product_id: card.id, image_index: next });
    }

    enter(card) {
      track('offer_impression', { product_id: card.id });
      if (this.reducedMotion()) return;
      card.el.classList.add('is-entering');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          card.el.classList.remove('is-entering');
        }),
      );
    }

    selectVariant(card, variantId) {
      const variant = this.optionOf(card).variants.find((v) => String(v.id) === String(variantId));
      if (!variant?.available) return;
      this.state.variants[card.id] = variant.id;
      this.saveState();
      this.render();
      track('deal_change', { product_id: card.id, variant_id: variant.id });
    }

    // A new flavor keeps the shopper's bundle size when that flavor sells it.
    selectFlavor(card, optionId) {
      const option = card.data.options.find((o) => String(o.id) === String(optionId));
      if (!option?.available || option === this.optionOf(card)) return;
      const count = bottlesOf(this.variantOf(card).title);
      const same = option.variants.find((v) => v.available && count && bottlesOf(v.title) === count);
      this.state.flavors[card.id] = option.id;
      if (same) this.state.variants[card.id] = same.id;
      else delete this.state.variants[card.id];
      this.state.gal[card.id] = 0;
      this.saveState();
      this.render();
      track('flavor_change', { product_id: card.id, flavor_product_id: option.id });
    }

    handlePointerDown(card, e) {
      if (card !== this.current() || card.pending || this.isOverlayOpen()) return;
      if (e.button > 0) return;
      if (e.target.closest(INTERACTIVE)) return;
      if (e.pointerType !== 'mouse' && (e.clientX < EDGE_GUARD || e.clientX > window.innerWidth - EDGE_GUARD)) return;
      const galleryRect = card.media && e.target.closest('[data-gallery]') ? card.media.getBoundingClientRect() : null;
      this.drag = {
        card,
        x0: e.clientX,
        y0: e.clientY,
        dx: 0,
        lock: null,
        width: card.el.offsetWidth,
        pointerId: e.pointerId,
        galleryRect,
      };
    }

    handlePointerMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.x0;
      const dy = e.clientY - d.y0;
      if (!d.lock) {
        if (Math.hypot(dx, dy) < LOCK_DISTANCE) return;
        d.lock = Math.abs(dx) > Math.abs(dy) * HORIZONTAL_BIAS ? 'x' : 'y';
        if (d.lock === 'y') {
          this.drag = null;
          return;
        }
        try {
          d.card.el.setPointerCapture(d.pointerId);
        } catch {
          // Throws if the pointer was already released; the drag still works without capture.
        }
        d.card.el.classList.add('is-dragging');
        track('swipe_start', { product_id: d.card.id });
      }
      e.preventDefault();
      const blocked = dx > 0 && this.isSoldOut(d.card);
      d.dx = blocked ? dx * RESIST : dx;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.paintDrag(d));
    }

    paintDrag(d) {
      const limit = d.width * this.threshold;
      const progress = Math.min(1, Math.abs(d.dx) / limit);
      const rotate = this.reducedMotion() ? 0 : Math.max(-6, Math.min(6, (d.dx / d.width) * 14));
      d.card.el.style.transform = 'translateX(' + d.dx + 'px) rotate(' + rotate + 'deg)';
      const pass = d.card.el.querySelector('[data-stamp="pass"]');
      const add = d.card.el.querySelector('[data-stamp="add"]');
      const adding = d.dx > 0 && !this.isSoldOut(d.card);
      pass.style.opacity = d.dx < 0 ? progress : 0;
      add.style.opacity = adding ? progress : 0;
      d.card.el.dataset.drag = d.dx < 0 ? 'pass' : adding ? 'add' : '';
      d.card.el.style.setProperty('--drag', progress);
      const past = progress >= 1;
      pass.querySelector('[data-stamp-text]').textContent = (past && this.text.stampPassRelease) || this.text.stampPass;
      add.querySelector('[data-stamp-text]').textContent = (past && this.text.stampAddRelease) || this.text.stampAdd;
    }

    resetDrag(card) {
      card.el.classList.remove('is-dragging');
      card.el.style.transform = '';
      card.el.style.removeProperty('--drag');
      delete card.el.dataset.drag;
      card.el.querySelectorAll('[data-stamp]').forEach((s) => {
        s.style.opacity = 0;
      });
    }

    handlePointerUp(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      this.drag = null;
      cancelAnimationFrame(this.raf);

      if (d.lock !== 'x') {
        if (!d.lock && d.galleryRect && e.type === 'pointerup') {
          const left = e.clientX - d.galleryRect.left < d.galleryRect.width / 2;
          this.setGallery(d.card, (this.state.gal[d.card.id] || 0) + (left ? -1 : 1));
        }
        return;
      }

      const limit = d.width * this.threshold;
      if (d.dx <= -limit) {
        this.pass(d.card, 'swipe');
      } else if (d.dx >= limit) {
        this.openDeal(d.card, 'swipe');
      } else {
        this.resetDrag(d.card);
        track('swipe_cancel', { product_id: d.card.id });
        if (d.dx > 0 && this.isSoldOut(d.card)) this.explainSoldOut(d.card);
      }
    }

    handleCardKey(card, e) {
      if (e.target !== card.el) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.pass(card, 'keyboard');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.openDeal(card, 'keyboard');
      }
    }

    handleDocumentKey(e) {
      if (this.viewer) {
        if (e.key === 'Escape') this.closeViewer();
        else if (e.key === 'ArrowLeft') this.stepViewer(-1);
        else if (e.key === 'ArrowRight') this.stepViewer(1);
        else if (e.key === 'Tab') this.trapFocus(e, this.q('[data-viewer]'));
        return;
      }
      if (this.sheet) {
        if (e.key === 'Escape') this.closeSheet();
        else if (e.key === 'Tab') this.trapFocus(e, this.q('[data-sheet]'));
      }
    }

    trapFocus(e, container) {
      const focusable = Array.from(
        container.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]'),
      ).filter((n) => n.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    isOverlayOpen() {
      return !!this.viewer || !!this.sheet;
    }

    explainSoldOut(card) {
      this.toast({ kind: 'error', message: this.text.soldOutToast });
      this.announce(card.data.title + ' is sold out. Swipe left to pass.');
    }

    exit(card, direction, done) {
      if (this.reducedMotion()) {
        card.el.style.transition = 'opacity 200ms linear';
        card.el.style.opacity = '0';
      } else {
        card.el.style.transition = 'transform ' + EXIT_MS + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
        card.el.style.transform =
          'translateX(' +
          (direction === 'right' ? 125 : -125) +
          '%) rotate(' +
          (direction === 'right' ? 8 : -8) +
          'deg)';
      }
      card.el.classList.remove('is-dragging');
      setTimeout(
        () => {
          card.el.style.transition = '';
          card.el.style.opacity = '';
          this.resetDrag(card);
          done();
        },
        this.reducedMotion() ? 200 : EXIT_MS,
      );
    }

    lowestPrice(card) {
      const option = this.optionOf(card);
      const prices = option.available ? option.variants.filter((v) => v.available).map((v) => v.price) : [];
      return prices.length ? Math.min(...prices) : Infinity;
    }

    // After a big add, shoppers rarely add several more full-price offers, so cheaper products come next.
    promoteUpsells(added, cents) {
      if (!this.upsellTrigger || cents < this.upsellTrigger) return;
      const open = (id) => id !== added.id && !this.state.decisions[id];
      const cheap = this.state.order.filter((id) => open(id) && this.lowestPrice(this.byId.get(id)) <= this.upsellMax);
      if (!cheap.length) return;
      const rest = this.state.order.filter((id) => !cheap.includes(id));
      const at = rest.findIndex(open);
      rest.splice(at < 0 ? rest.length : at, 0, ...cheap);
      this.state.order = rest;
      track('upsell_order', { product_id: added.id, promoted: cheap.length });
    }

    moveToFront(id) {
      const order = this.state.order.filter((x) => x !== id);
      const at = order.findIndex((x) => !this.state.decisions[x]);
      order.splice(at < 0 ? order.length : at, 0, id);
      this.state.order = order;
    }

    pass(card, source) {
      if (card !== this.current() || card.pending) return;
      this.exit(card, 'left', () => {
        this.state.decisions[card.id] = 'passed';
        this.saveState();
        this.render();
        this.focusCurrent();
        this.toast({ kind: 'info', message: 'Passed for now', action: 'Undo', onAction: () => this.undoPass(card) });
        this.announce('Passed for now. Moved to Skipped.');
      });
      track('offer_pass', { product_id: card.id, source });
    }

    undoPass(card) {
      delete this.state.decisions[card.id];
      this.moveToFront(card.id);
      this.saveState();
      this.hideToast();
      this.render();
      this.focusCurrent();
      this.announce(card.data.title + ' is back in the feed.');
      track('undo', { product_id: card.id, undo_of: 'pass' });
    }

    openDeal(card, source, trigger) {
      if (card !== this.current() || card.pending) return;
      this.resetDrag(card);
      if (this.isSoldOut(card)) {
        this.explainSoldOut(card);
        return;
      }
      const body = this.q('[data-offer-template]').content.firstElementChild.cloneNode(true);
      const foot = el('div', this.c('deal-foot'));
      const summary = el('div', this.c('deal-summary'));
      summary.append(el('strong', this.c('deal-summary-name')), el('span', this.c('deal-summary-meta')));
      foot.append(summary, el('strong', this.c('deal-summary-total')));
      const add = el('button', this.c('button') + ' ' + this.c('button-solid'), this.text.dealAdd);
      add.type = 'button';
      add.dataset.action = 'deal-add';
      const footer = el('div');
      footer.append(foot, add);
      this.openSheet({
        kind: 'deal',
        card,
        title: this.text.dealTitle,
        body,
        foot: footer,
        trigger: trigger || card.el,
      });
      this.renderDealSheet();
      track('deal_open', { product_id: card.id, source });
    }

    renderDealSheet() {
      const card = this.sheet.card;
      const sheet = this.q('[data-sheet]');
      const option = this.optionOf(card);
      const variant = this.variantOf(card);
      this.renderOffer(sheet, card);
      this.q('[data-sheet-sub]').textContent = this.qualifiesForFreeShipping(variant.price)
        ? this.text.freeShipping
        : '';
      this.q('[data-sheet-sub]').hidden = !this.qualifiesForFreeShipping(variant.price);
      let name = this.dealName(variant);
      if (card.data.options.length > 1) name += ' · ' + option.label;
      const meta = [];
      const count = bottlesOf(variant.title);
      if (count > 1) meta.push(this.money(Math.round(variant.price / count)) + ' each');
      const gifts = this.giftNames().length;
      if (gifts && this.giftsApply(card, variant))
        meta.push(gifts + (gifts === 1 ? ' free gift included' : ' free gifts included'));
      sheet.querySelector('.' + this.c('deal-summary-name')).textContent = name;
      sheet.querySelector('.' + this.c('deal-summary-meta')).textContent = meta.join(' · ');
      sheet.querySelector('.' + this.c('deal-summary-total')).textContent = variant.priceText;
    }

    addFromDeal() {
      const card = this.sheet?.card;
      if (!card) return;
      this.closeSheet(false);
      this.add(card, 'deal').catch(() => {});
    }

    async add(card, source) {
      if (card.pending || this.isSoldOut(card)) return false;
      const variant = this.variantOf(card);
      const fromFeed = card === this.current();
      card.pending = true;
      this.announce('Adding ' + card.data.title + '…');
      track('add_attempt', { product_id: card.id, variant_id: variant.id, selling_plan: variant.planId, source });

      const ref = this.lineRef(card, variant);
      const request = this.enqueueAdd(ref);
      this.promoteUpsells(card, variant.price);

      // The card leaves the feed at once; the cart request runs behind the animation and a failed add brings the card back.
      let exited = Promise.resolve();
      if (fromFeed) {
        this.fly(card);
        exited = new Promise((resolve) => {
          this.exit(card, 'right', () => {
            this.state.decisions[card.id] = 'added';
            this.saveState();
            this.render();
            this.focusCurrent();
            resolve();
          });
        });
      }

      let previousQty;
      try {
        previousQty = await request;
      } catch (error) {
        await exited;
        card.pending = false;
        if (fromFeed) {
          delete this.state.decisions[card.id];
          this.moveToFront(card.id);
          this.saveState();
          this.render();
          this.focusCurrent();
        }
        this.toast({
          kind: 'error',
          message: 'Couldn’t add this offer',
          action: 'Retry',
          onAction: () => {
            this.hideToast();
            this.add(card, 'retry').catch(() => {});
          },
        });
        this.announce('Couldn’t add ' + card.data.title + '. Try again.');
        track('add_failure', { product_id: card.id, variant_id: variant.id, source, error: String(error?.message) });
        return false;
      }

      card.pending = false;
      track('add_success', { product_id: card.id, variant_id: variant.id, selling_plan: variant.planId, source });
      await exited;

      if (!fromFeed) {
        this.state.decisions[card.id] = 'added';
        this.saveState();
        this.render();
      }
      const record = { card, ref, previousQty, fromFeed };
      this.toast({ kind: 'add', message: 'Added to your bag', action: 'Undo', onAction: () => this.undoAdd(record) });
      this.announce('Added to your bag. ' + card.data.title + ', ' + variant.priceText + '.');
      this.pulseBag();
      return true;
    }

    // Adds run one at a time: each reads the cart first to decide whether the gift rides along and what quantity Undo restores.
    enqueueAdd(ref) {
      const run = this.addQueue.then(() => this.postAdd(ref));
      this.addQueue = run.catch(() => {});
      return run;
    }

    async postAdd(ref) {
      const before = await this.fetchCart();
      const previousLine = before.items.find((i) => this.matchesLine(i, ref));
      const previousQty = previousLine ? previousLine.quantity : 0;
      const giftItems = before.items.some((i) => this.isGiftLine(i)) ? [] : ref.giftItems;

      addsInFlight++;
      let response;
      let json;
      try {
        response = await fetch(this.root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [ref.item, ...giftItems], sections: 'cart-icon-bubble' }),
        });
        json = await response.json().catch(() => ({}));
      } finally {
        addsInFlight--;
      }
      if (!response.ok || json.status) throw new Error(json.description || json.message || 'Add failed');

      this.updateBubble(json.sections);
      const hasLine = (c) => c.items.some((i) => this.matchesLine(i, ref) && i.quantity > previousQty);
      this.refreshCart()
        .then(() => {
          if (this.cart && !hasLine(this.cart)) return this.settleCart(hasLine);
        })
        .catch(() => {});
      this.syncUpcart();
      return previousQty;
    }

    async undoAdd(record) {
      const { card, ref, previousQty, fromFeed } = record;
      this.hideToast();
      try {
        const json = await this.changeLine(ref, previousQty);
        this.updateBubble(json.sections);
        await this.refreshCart();
        this.syncUpcart();
        this.confirmQuantity(ref, previousQty)
          .then(() => this.restoreGift())
          .then(() => this.refreshCart())
          .then(() => this.syncUpcart())
          .catch(() => {});
        delete this.state.decisions[card.id];
        if (fromFeed) this.moveToFront(card.id);
        else this.state.decisions[card.id] = 'passed';
        this.saveState();
        this.render();
        this.focusCurrent();
        this.toast({ kind: 'info', message: 'Undone' });
        this.announce(
          'Undone. ' +
            card.data.title +
            (previousQty > 0 ? ' quantity back to ' + previousQty + '.' : ' removed from your bag.'),
        );
        track('undo', { product_id: card.id, undo_of: 'add' });
      } catch {
        await this.refreshCart();
        this.toast({
          kind: 'error',
          message: 'Couldn’t undo',
          action: 'Retry',
          onAction: () => this.undoAdd(record),
        });
        this.announce('Couldn’t undo. Your bag hasn’t changed.');
        track('undo_failure', { product_id: card.id });
      }
    }

    reset() {
      const previous = structuredClone(this.state);
      this.state = {
        ...this.state,
        order: this.cards.map((c) => c.id),
        decisions: {},
        variants: {},
        flavors: {},
        gal: {},
      };
      this.saveState();
      this.render();
      this.focusCurrent();
      this.toast({
        kind: 'info',
        message: 'Feed reset',
        action: 'Undo',
        onAction: () => {
          this.state = previous;
          this.saveState();
          this.hideToast();
          this.render();
          this.focusCurrent();
          track('reset_undo');
        },
      });
      this.announce('Feed reset. Your bag hasn’t changed.');
      track('reset');
    }

    restart() {
      Object.keys(this.state.decisions).forEach((id) => {
        if (this.state.decisions[id] === 'passed') delete this.state.decisions[id];
      });
      this.saveState();
      this.render();
      this.focusCurrent();
      track('restart');
    }

    focusCurrent() {
      const current = this.current();
      if (current && this.root.contains(document.activeElement)) current.el.focus({ preventScroll: true });
    }

    // The theme's gift rules (cart-offers.js) rewrite the cart after adds, which can change
    // line keys and positions, so the line is looked up fresh and retried once.
    async changeLine(ref, quantity) {
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        const cart = await this.fetchCart();
        const line = cart.items.find((i) => this.matchesLine(i, ref));
        if (!line) throw new Error('Line not found');
        const response = await fetch(this.root.dataset.cartChangeUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity, sections: 'cart-icon-bubble' }),
        });
        const json = await response.json().catch(() => ({}));
        if (response.ok && !json.status) return json;
        lastError = new Error(json.description || 'Undo failed');
      }
      throw lastError;
    }

    // Other apps (UpCart rewards, Intelligems cart metafields) write to the cart right after an add; a write that overlaps ours can restore the old quantity, so check and repeat once.
    async confirmQuantity(ref, quantity) {
      await new Promise((resolve) => setTimeout(resolve, 900));
      const cart = await this.fetchCart();
      const line = cart.items.find((i) => this.matchesLine(i, ref));
      const current = line ? line.quantity : 0;
      if (current !== quantity) await this.changeLine(ref, quantity);
    }

    planOf(item) {
      return item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
    }

    async fetchCart() {
      const response = await fetch(this.root.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }

    async settleCart(isSettled) {
      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await this.refreshCart();
        if (!this.cart || isSettled(this.cart)) return;
      }
    }

    async refreshCart() {
      try {
        this.cart = await this.fetchCart();
      } catch {
        return;
      }
      this.render();
      if (this.sheet?.kind === 'bag' && !this.sheet.busy) this.fillBag();
    }

    wireUpcartEvents(attempt = 0) {
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        try {
          window.upcartSubscribeCartUpdated(() => this.refreshCart());
        } catch {
          // UpCart API failures must not break the swipe feed; the bag bar refreshes after our own adds.
        }
        return;
      }
      if (attempt < 40) setTimeout(() => this.wireUpcartEvents(attempt + 1), 250);
    }

    syncUpcart() {
      if (typeof window.upcartRefreshCart === 'function') {
        try {
          Promise.resolve(window.upcartRefreshCart()).catch(() => {});
        } catch {
          // Same as above: UpCart's drawer catches up on its next open.
        }
      }
    }

    updateBubble(sections) {
      const html = sections?.['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (parsed) bubble.innerHTML = parsed.innerHTML;
    }

    renderBagBar() {
      const bar = this.q('[data-bagbar]');
      const count = this.bagCount();
      bar.hidden = count === 0;
      this.root.classList.toggle('has-bag', count > 0);
      if (!count) return;
      this.q('[data-bag-count]').textContent = 'Your bag (' + count + ')';
      this.q('[data-bag-total]').textContent = this.money(this.cart.total_price);
      this.q('[data-bag-shipping]').hidden = !this.qualifiesForFreeShipping(this.cart.total_price);
    }

    pulseBag() {
      const inner = this.q('[data-bagbar-inner]');
      if (!inner || this.reducedMotion()) return;
      inner.classList.remove('is-pulsing');
      inner.getBoundingClientRect();
      inner.classList.add('is-pulsing');
    }

    fly(card) {
      if (this.reducedMotion()) return;
      const index = this.state.gal[card.id] || 0;
      const source = card.track.children[index]?.querySelector('img');
      const target = document.getElementById('cart-icon-bubble');
      if (!source || !target) return;
      const from = source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;
      const size = Math.min(from.width, from.height, 140);
      const ghost = el('img', this.c('fly'));
      ghost.src = source.currentSrc || source.src;
      ghost.alt = '';
      ghost.style.width = size + 'px';
      ghost.style.height = size + 'px';
      ghost.style.left = from.left + (from.width - size) / 2 + 'px';
      ghost.style.top = from.top + (from.height - size) / 2 + 'px';
      this.root.appendChild(ghost);
      const dx = to.left + to.width / 2 - (from.left + from.width / 2);
      const dy = to.top + to.height / 2 - (from.top + from.height / 2);
      requestAnimationFrame(() => {
        ghost.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(0.15)';
        ghost.style.opacity = '0.3';
      });
      setTimeout(() => ghost.remove(), 560);
    }

    toast(options) {
      const wrap = this.q('[data-toast]');
      const inner = this.q('[data-toast-inner]');
      const action = this.q('[data-toast-action]');
      clearTimeout(this.toastTimer);
      inner.dataset.kind = options.kind;
      this.q('[data-toast-message]').textContent = options.message;
      action.hidden = !options.action;
      action.textContent = options.action || '';
      action.onclick = options.onAction ? () => options.onAction() : null;
      wrap.hidden = false;
      inner.style.animation = 'none';
      inner.getBoundingClientRect();
      inner.style.animation = '';
      this.toastTimer = setTimeout(() => this.hideToast(), options.kind === 'error' ? TOAST_MS * 1.6 : TOAST_MS);
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      this.q('[data-toast]').hidden = true;
    }

    announce(text) {
      const region = this.q('[data-announce]');
      region.textContent = '';
      setTimeout(() => {
        region.textContent = text;
      }, 40);
    }

    openSheet({ kind, card = null, title, sub = '', body, foot = null, trigger }) {
      if (!this.sheet) this.lastFocus = trigger || document.activeElement;
      this.sheet = { kind, card, busy: false };
      const sheet = this.q('[data-sheet]');
      sheet.dataset.kind = kind;
      this.q('[data-sheet-title]').textContent = title;
      const subEl = this.q('[data-sheet-sub]');
      subEl.textContent = sub;
      subEl.hidden = !sub;
      this.q('[data-sheet-body]').replaceChildren(body);
      const footEl = this.q('[data-sheet-foot]');
      footEl.replaceChildren(...(foot ? [foot] : []));
      footEl.hidden = !foot;
      sheet.hidden = false;
      this.q('[data-sheet-overlay]').hidden = false;
      this.q('[data-sheet-body]').scrollTop = 0;
      document.documentElement.style.overflow = 'hidden';
      this.q('[data-sheet] [data-action="close-sheet"]').focus();
    }

    bindSheetDrag() {
      const sheet = this.q('[data-sheet]');
      const head = sheet.querySelector('.' + this.c('sheet-head'));
      head.addEventListener('pointerdown', (e) => {
        if (!this.sheet || e.button > 0 || e.target.closest('button')) return;
        const startY = e.clientY;
        const startTime = performance.now();
        let distance = 0;
        head.setPointerCapture(e.pointerId);
        sheet.style.animation = 'none';
        sheet.style.transition = 'none';
        const move = (ev) => {
          distance = Math.max(0, ev.clientY - startY);
          sheet.style.transform = 'translateY(' + distance + 'px)';
        };
        const end = () => {
          head.removeEventListener('pointermove', move);
          head.removeEventListener('pointerup', end);
          head.removeEventListener('pointercancel', end);
          const flick = distance > 30 && distance / Math.max(1, performance.now() - startTime) > 0.6;
          const close = flick || distance > Math.min(140, sheet.offsetHeight * 0.3);
          sheet.style.transition = 'transform 200ms var(--swipe-ease)';
          sheet.style.transform = close ? 'translateY(100%)' : '';
          setTimeout(() => {
            if (close) this.closeSheet();
            sheet.style.transition = '';
            sheet.style.transform = '';
            sheet.style.animation = '';
          }, 200);
        };
        head.addEventListener('pointermove', move);
        head.addEventListener('pointerup', end);
        head.addEventListener('pointercancel', end);
      });
    }

    closeSheet(restoreFocus = true) {
      if (!this.sheet) return;
      this.sheet = null;
      this.q('[data-sheet]').hidden = true;
      this.q('[data-sheet-overlay]').hidden = true;
      document.documentElement.style.overflow = '';
      if (restoreFocus) {
        const back =
          this.lastFocus && document.contains(this.lastFocus) && this.lastFocus.offsetParent !== null
            ? this.lastFocus
            : this.current()?.el;
        if (back) back.focus({ preventScroll: true });
      }
      this.lastFocus = null;
    }

    openBag(trigger) {
      track('bag_review');
      const foot = el('div');
      const row = el('div', this.c('bag-subtotal'));
      const label = el('div');
      label.append(el('strong', '', 'Subtotal'), el('span', '', 'Shipping & taxes at checkout'));
      row.append(label, el('strong', this.c('bag-subtotal-total')));
      const checkout = el('a', this.c('button') + ' ' + this.c('button-solid'), 'Checkout');
      checkout.href = this.root.dataset.checkoutUrl;
      checkout.addEventListener('click', () => track('checkout_click'));
      const keep = el('button', this.c('link-button'), 'Keep exploring');
      keep.type = 'button';
      keep.dataset.action = 'close-sheet';
      foot.append(row, checkout, keep);
      this.openSheet({ kind: 'bag', title: 'Your bag', body: el('div', this.c('bag-list')), foot, trigger });
      this.fillBag();
      if (!this.cart) this.refreshCart();
    }

    fillBag() {
      const items = this.cart ? this.cart.items : [];
      const count = this.bagCount();
      const total = this.cart ? this.cart.total_price : 0;
      this.q('[data-sheet-title]').textContent = 'Your bag (' + count + ')';
      const sub = this.q('[data-sheet-sub]');
      sub.textContent = this.text.freeShipping;
      sub.hidden = !this.qualifiesForFreeShipping(total);
      const totalEl = this.q('.' + this.c('bag-subtotal-total'));
      if (totalEl) totalEl.textContent = this.money(total);
      const checkout = this.q('[data-sheet-foot] a');
      if (checkout) checkout.hidden = count === 0;

      const list = this.q('.' + this.c('bag-list'));
      if (!list) return;
      if (!items.length) {
        list.replaceChildren(
          el('p', this.c('sheet-empty'), 'Your bag is empty. Swipe right on a product to choose your deal.'),
        );
        return;
      }
      list.replaceChildren(...items.map((item) => this.bagLine(item)));
    }

    bagLine(item) {
      const gift = this.isGiftLine(item);
      const line = el('div', this.c('bag-line'));
      const top = el('div', this.c('bag-line-top'));
      if (item.image) {
        const img = el('img', this.c('bag-image'));
        img.src = item.image.replace(/(\.\w+)(\?|$)/, '_200x$1$2');
        img.alt = '';
        img.width = 60;
        img.height = 60;
        top.appendChild(img);
      }
      const info = el('div', this.c('bag-info'));
      info.appendChild(el('span', this.c('bag-name'), item.product_title));
      const meta = el('span', this.c('bag-meta'));
      if (item.variant_title) meta.appendChild(el('strong', '', item.variant_title));
      const bottles = bottlesOf(item.variant_title);
      if (!gift && bottles > 1 && item.quantity) {
        meta.appendChild(el('span', this.c('bag-dot'), '•'));
        meta.appendChild(
          el('span', '', this.money(Math.round(item.final_line_price / item.quantity / bottles)) + '/each'),
        );
      }
      if (meta.childNodes.length) info.appendChild(meta);
      let plan = 'One-time purchase';
      if (gift) plan = item.final_line_price ? 'Gift' : 'Free gift';
      else if (item.selling_plan_allocation) plan = 'Subscription';
      info.appendChild(el('span', this.c('bag-plan'), plan));
      top.append(
        info,
        el('strong', this.c('bag-price'), item.final_line_price ? this.money(item.final_line_price) : 'FREE'),
      );
      line.appendChild(top);
      if (gift) return line;

      const controls = el('div', this.c('bag-controls'));
      const remove = el('button', this.c('link-button'), 'Remove');
      remove.type = 'button';
      remove.dataset.action = 'bag-qty';
      remove.dataset.key = item.key;
      remove.dataset.quantity = 0;
      remove.setAttribute('aria-label', 'Remove ' + item.product_title);
      const stepper = el('div', this.c('stepper'));
      const minus = el('button', '', '−');
      minus.type = 'button';
      minus.dataset.action = 'bag-qty';
      minus.dataset.key = item.key;
      minus.dataset.quantity = item.quantity - 1;
      minus.setAttribute('aria-label', 'Decrease quantity of ' + item.product_title);
      const plus = el('button', '', '+');
      plus.type = 'button';
      plus.dataset.action = 'bag-qty';
      plus.dataset.key = item.key;
      plus.dataset.quantity = item.quantity + 1;
      plus.setAttribute('aria-label', 'Increase quantity of ' + item.product_title);
      const qty = el('span', '', String(item.quantity));
      qty.setAttribute('aria-label', 'Quantity ' + item.quantity);
      stepper.append(minus, qty, plus);
      controls.append(remove, stepper);
      line.appendChild(controls);
      return line;
    }

    async changeQuantity(key, quantity) {
      if (!this.sheet || this.sheet.busy || !key || quantity < 0) return;
      this.sheet.busy = true;
      const list = this.q('.' + this.c('bag-list'));
      list?.setAttribute('aria-busy', 'true');
      this.q('[data-sheet]')
        .querySelectorAll('[data-action="bag-qty"]')
        .forEach((btn) => {
          btn.disabled = true;
        });
      try {
        const response = await fetch(this.root.dataset.cartChangeUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: 'cart-icon-bubble' }),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok || json.status) throw new Error(json.description || 'Change failed');
        this.updateBubble(json.sections);
        // Removing the line that carries the gift takes the gift with it; put it back under another qualifying line.
        if (quantity === 0) await this.restoreGift().catch(() => {});
        track('bag_change', { quantity });
      } catch {
        this.toast({ kind: 'error', message: 'Couldn’t update your bag' });
      }
      if (this.sheet) this.sheet.busy = false;
      list?.removeAttribute('aria-busy');
      await this.refreshCart();
      if (this.sheet?.kind === 'bag') this.fillBag();
      this.syncUpcart();
      this.announce('Bag updated. ' + this.bagCount() + ' items.');
    }

    openSkipped(trigger) {
      track('skipped_open');
      const back = el('button', this.c('button') + ' ' + this.c('button-solid'), 'Back to feed');
      back.type = 'button';
      back.dataset.action = 'close-sheet';
      this.openSheet({
        kind: 'skipped',
        title: 'Skipped',
        sub: this.text.skippedSub,
        body: el('div', this.c('skipped-list')),
        foot: back,
        trigger,
      });
      this.fillSkipped();
    }

    fillSkipped() {
      const passed = this.passedCards();
      this.q('[data-sheet-title]').textContent = 'Skipped (' + passed.length + ')';
      const list = this.q('.' + this.c('skipped-list'));
      if (!passed.length) {
        list.replaceChildren(
          el(
            'p',
            this.c('sheet-empty'),
            'Nothing skipped yet. Products you pass appear here so you can come back to them.',
          ),
        );
        return;
      }
      list.replaceChildren(
        ...passed.map((card) => {
          const option = this.optionOf(card);
          const variant = this.variantOf(card);
          const row = el('div', this.c('skipped-row'));
          if (option.image) {
            const img = el('img', this.c('skipped-image'));
            img.src = option.image;
            img.alt = '';
            img.width = 60;
            img.height = 60;
            row.appendChild(img);
          }
          const info = el('div', this.c('skipped-info'));
          info.append(
            el('span', this.c('skipped-name'), option.title),
            el('span', this.c('skipped-price'), variant.priceText),
          );
          const soldOut = this.isSoldOut(card);
          const add = el(
            'button',
            this.c('button') + ' ' + this.c('button-cta'),
            soldOut ? this.text.soldOut : 'Add to bag',
          );
          add.type = 'button';
          add.dataset.action = 'skipped-add';
          add.dataset.productId = card.id;
          add.disabled = soldOut;
          row.append(info, add);
          return row;
        }),
      );
    }

    async addSkipped(button) {
      const card = this.byId.get(button.dataset.productId);
      if (!card) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      track('skipped_recover', { product_id: card.id });
      const ok = await this.add(card, 'skipped');
      if (this.sheet?.kind !== 'skipped') return;
      if (ok) this.fillSkipped();
      else {
        button.disabled = false;
        button.textContent = 'Add to bag';
      }
    }

    openViewer(card, trigger) {
      const images = this.optionOf(card).images;
      if (!images.length) return;
      this.showViewer({ card, images, index: this.state.gal[card.id] || 0, trigger });
      track('viewer_open', { product_id: card.id });
    }

    openNutrition(card, trigger) {
      const label = this.optionOf(card).nutrition;
      if (!label) return;
      this.showViewer({ card: null, images: [label], index: 0, trigger });
      track('nutrition_open', { product_id: card.id });
    }

    showViewer(viewer) {
      this.viewer = viewer;
      this.q('[data-viewer]').hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.renderViewer();
      this.q('[data-viewer] [data-action="close-viewer"]').focus();
    }

    renderViewer() {
      const { images, index } = this.viewer;
      const image = images[index];
      const img = this.q('[data-viewer-image]');
      img.src = image.src;
      img.alt = image.alt;
      this.q('[data-viewer-caption]').textContent = image.alt;
      this.q('[data-viewer-counter]').textContent = images.length > 1 ? index + 1 + ' / ' + images.length : '';
      const prev = this.q('[data-action="viewer-prev"]');
      const next = this.q('[data-action="viewer-next"]');
      prev.disabled = index === 0;
      next.disabled = index === images.length - 1;
      prev.hidden = images.length < 2;
      next.hidden = images.length < 2;
    }

    stepViewer(delta) {
      if (!this.viewer) return;
      const count = this.viewer.images.length;
      const next = Math.max(0, Math.min(count - 1, this.viewer.index + delta));
      if (next === this.viewer.index) return;
      this.viewer.index = next;
      if (this.viewer.card) this.setGallery(this.viewer.card, next);
      this.renderViewer();
    }

    closeViewer() {
      if (!this.viewer) return;
      const trigger = this.viewer.trigger;
      this.viewer = null;
      this.q('[data-viewer]').hidden = true;
      if (!this.sheet) document.documentElement.style.overflow = '';
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    }
  }

  const instances = new Map();

  // The section is hidden on desktop, and the page can hide it too, so a feed starts only once its section is actually shown.
  const visibility = new ResizeObserver((entries) => {
    if (entries.some((entry) => entry.target.getClientRects().length)) initAll();
  });

  function initAll() {
    document.querySelectorAll('.custom-swipe-match').forEach((root) => {
      if (root.dataset.initialised === 'true') return;
      if (!root.getClientRects().length) {
        visibility.observe(root);
        return;
      }
      visibility.unobserve(root);
      hookUpcart();
      root.dataset.initialised = 'true';
      const instance = new SwipeMatch(root);
      instances.set(root.dataset.sectionId, instance);
      instance.start().catch(() => {});
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (event) => {
    const instance = instances.get(event.detail.sectionId);
    if (instance) instance.destroy();
    instances.delete(event.detail.sectionId);
    initAll();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const instance = instances.get(event.detail.sectionId);
    if (instance) instance.destroy();
    instances.delete(event.detail.sectionId);
  });
})();
